import type { Component, VoidComponent } from "solid-js";
import { createMemo, For, Match, Show, Switch } from "solid-js";
import type { RouteSectionProps } from "@solidjs/router";
import withPagePermissionCheck from "../../utils/withPagePermissionCheck";
import PermissionService from "../../services/PermissionService";
import { useCustomerState } from "../../state/customer";
import { useTranslation } from "../../state/i18n";
import ApiService from "../../services/ApiService";
import EmptyState from "../../components/emptyState/EmptyState";
import Text from "../../components/text/Text";
import { IconButton, TextAndIconButton } from "../../components/button/Button";
import { MAX_DATA_SOURCES } from "../../constants";
import { isEmpty } from "lodash-es";
import InlineMessage from "../../components/inlineMessage/InlineMessage";
import { Icon } from "../../components/icon/Icon";
import FormatDate from "../../components/formatting/FormatDate";
import Pill from "../../components/pill/Pill";
import { useModalManager } from "../../components/modal/ModalManager";
import { ListItemMenu, type ListItemMenuItem } from "../../components/listItemMenu/ListItemMenu";
import type { TFunction } from "i18next";
import type { DataSourceType } from "../../types/DataSource";
import { dataSourcesByType } from "./dataSource/dataSourcesByType";
import DeleteDataSourceConfirmationModal from "./DeleteDataSourceConfirmationModal";
import { IconTile } from "../../components/icon/IconTile";
import CreateDataSourceModal from "./dataSource/CreateDataSourceModal";

const PAGE_TITLE_ID = "data-source-page-header";

const buildCreateDataSourceButtonItems = (t: TFunction, createDataSource: (type: DataSourceType) => void) =>
  [
    Object.values(dataSourcesByType).map((definition) => ({
      value: `create-${definition.type}-data-source`,
      type: "action",
      variant: "icon",
      label: t(definition.name),
      icon: { name: definition.icon, type: "solid", class: "icon-primary" },
      onClick: () => createDataSource(definition.type),
    })),
  ] satisfies ListItemMenuItem<string>[][];

const DataSourcesPage: Component<RouteSectionProps<never>> = () => {
  const { t } = useTranslation();
  const { open } = useModalManager();
  const customer = useCustomerState();
  const dataSourcesQuery = ApiService.getDataSources();
  const hasReachedDataSourceLimit = createMemo(() =>
    dataSourcesQuery.status === "success" ? dataSourcesQuery.data.length >= MAX_DATA_SOURCES : false,
  );

  const openCreateDataSourceModal = (type: DataSourceType) => {
    open({
      content: () => <CreateDataSourceModal type={type} />,
    });
  };

  return (
    <div class="flex h-full flex-col">
      <div class="mb-6 flex justify-between pb-3">
        <Text
          id={PAGE_TITLE_ID}
          text={t(($) => $.DATASOURCES_PAGE_TITLE)}
          as="h1"
          class="text-header-xl text-primary"
        />
        <Show when={!isEmpty(dataSourcesQuery.data) && !hasReachedDataSourceLimit()}>
          <ListItemMenu
            menu={{
              size: "md",
              positioning: {
                placement: "bottom-end",
              },
            }}
            trigger={(menuTriggerProps) => (
              <TextAndIconButton
                variant="light"
                size="large"
                text={t(($) => $.DATASOURCES_PAGE_ACTION_CREATE)}
                icon="plus"
                {...menuTriggerProps}
              />
            )}
            items={buildCreateDataSourceButtonItems(t, openCreateDataSourceModal)}
          />
        </Show>
      </div>

      <Show when={hasReachedDataSourceLimit()}>
        <div class="pb-4">
          <InlineMessage
            variant="warning"
            label={t(($) => $.DATASOURCES_PAGE_LIMIT_REACHED, { limit: MAX_DATA_SOURCES })}
          />
        </div>
      </Show>

      <div class="relative h-full min-h-0" aria-busy={dataSourcesQuery.isFetching}>
        <Show
          when={dataSourcesQuery.data}
          fallback={
            <div class="absolute flex size-full flex-col items-center justify-center bg-surface-default opacity-75">
              <Icon name="spinner" size="2xl" type="solid" class="animate-spin icon-secondary" />
              <span class="text-medium-regular text-primary">{t(($) => $.DATASOURCES_PAGE_LOADING_TITLE)}</span>
            </div>
          }
        >
          {(data) => (
            <div class="h-full">
              <Show when={!isEmpty(data())} fallback={<NoDataSources actionOnClick={openCreateDataSourceModal} />}>
                <table
                  aria-labelledby={PAGE_TITLE_ID}
                  style={{
                    "--column-template": "minmax(120px, 1fr) minmax(164px, 184px) minmax(164px, 184px) 38px",
                  }}
                  class="flex size-full border-collapse flex-col overflow-hidden"
                >
                  <thead>
                    <tr class="grid grid-cols-template gap-6 border-b border-subtle px-3 py-1.5 text-base-semibold text-secondary">
                      <th class="sr-only">{t(($) => $.DATASOURCES_PAGE_TABLE_HEADER_VIEW)}</th>
                      <th>{t(($) => $.DATASOURCES_PAGE_TABLE_HEADER_NAME)}</th>
                      <th>{t(($) => $.DATASOURCES_PAGE_TABLE_HEADER_LAST_IMPORTED)}</th>
                      <th>{t(($) => $.DATASOURCES_PAGE_TABLE_HEADER_STATUS)}</th>
                      <th class="sr-only">{t(($) => $.DATASOURCES_PAGE_TABLE_HEADER_ACTIONS)}</th>
                    </tr>
                  </thead>
                  <tbody class="h-full overflow-y-auto pb-6">
                    <For each={data()}>
                      {(dataSource) => {
                        const definition = dataSourcesByType[dataSource.type];

                        return (
                          <tr class="pointer-events-none relative grid grid-cols-template items-center gap-6 overflow-hidden bg-surface-default p-3 text-base-regular text-secondary [&:not(:has([class*='bg-']:hover))]:hover:bg-surface-hover">
                            <td class="pointer-events-auto absolute inset-0 w-full">
                              <a
                                href={`/customers/${customer.customerId}/data-sources/${dataSource.id}`}
                                class="absolute inset-0 cursor-pointer -outline-offset-1 focus-visible:outline-default"
                                aria-label={t(($) => $.DATASOURCES_PAGE_ACTION_VIEW, { name: dataSource.name })}
                              />
                            </td>

                            <td class="flex items-center gap-1.5">
                              <IconTile icon={definition.icon} size="sm" border />
                              <Text class="text-base-medium text-primary" text={dataSource.name} truncate />
                            </td>
                            <td>
                              <Show when={dataSource.lastUpdatedAt}>
                                {(lastUpdatedAt) => <FormatDate value={lastUpdatedAt()} />}
                              </Show>
                            </td>
                            <td>
                              <div>
                                <Switch>
                                  <Match when={dataSource.updateStatus === "SUCCESS"}>
                                    <Pill
                                      variant="success"
                                      label={t(($) => $.DATASOURCES_PAGE_DATASOURCE_IMPORT_STATUS_SUCCESS)}
                                    />
                                  </Match>
                                  <Match when={dataSource.updateStatus === "FAILURE"}>
                                    <Pill
                                      variant="danger"
                                      label={t(($) => $.DATASOURCES_PAGE_DATASOURCE_IMPORT_STATUS_FAILURE)}
                                    />
                                  </Match>
                                </Switch>
                              </div>

                              <Show when={!isEmpty(dataSource.lastUpdateWarningMessages)}>
                                <div>
                                  <Pill
                                    variant="warning"
                                    label={t(($) => $.DATASOURCE_IMPORT_WARNINGS, {
                                      count: dataSource.lastUpdateWarningMessages.length,
                                    })}
                                  />
                                </div>
                              </Show>
                            </td>
                            <td class="pointer-events-auto relative">
                              <IconButton
                                variant="tertiary"
                                size="medium"
                                icon="trash"
                                aria-label={t(($) => $.DATASOURCES_PAGE_ACTION_DELETE, { name: dataSource.name })}
                                onClick={() => {
                                  open({
                                    content: () => (
                                      <DeleteDataSourceConfirmationModal
                                        customerId={customer.customerId}
                                        dataSourceId={dataSource.id}
                                      />
                                    ),
                                  });
                                }}
                              />
                            </td>
                          </tr>
                        );
                      }}
                    </For>
                  </tbody>
                </table>
              </Show>
            </div>
          )}
        </Show>
      </div>
    </div>
  );
};

type NoDataSourcesProps = {
  actionOnClick: (type: DataSourceType) => void;
};
const NoDataSources: VoidComponent<NoDataSourcesProps> = (props) => {
  const { t } = useTranslation();

  return (
    <EmptyState
      title={t(($) => $.DATASOURCES_PAGE_EMPTY_STATE_TITLE)}
      description={t(($) => $.DATASOURCES_PAGE_EMPTY_STATE_DESCRIPTION)}
      illustration={{ primaryIcon: "database" }}
      action={{
        icon: "plus",
        text: t(($) => $.DATASOURCES_PAGE_EMPTY_STATE_ACTION),
        variant: "light",
        items: buildCreateDataSourceButtonItems(t, props.actionOnClick),
      }}
    />
  );
};

const ProtectedDataSourcesPage = withPagePermissionCheck(DataSourcesPage, PermissionService.canManageDataSources);

export default ProtectedDataSourcesPage;
